import { Marker, type MapRef } from "@vis.gl/react-maplibre";
import { useMemo, useRef, useState, type FC } from "react";
import useEvent from "react-use-event-hook";

import {
  MAPLIBRE_PICKED_ADDRESS_PIN_COLOR,
  MAPLIBRE_PICKED_ADDRESS_PIN_GLYPH,
  MapLibreStationPinMarker,
  useMapLibreLocationsAutoFit,
} from "@ryde/controls";
import {
  defaultMapCenter,
  isSameCoordinate,
  MarkerIconType,
  type ICoordinate,
  type ILocationInfo,
} from "@ryde/shared";

import { LandingAddressMapPopup } from "./landing-address-map-popup";
import { LandingAddressMapStyled } from "./landing-address-map.styled";
import {
  DEFAULT_LANDING_ADDRESS_MAP_HEIGHT,
  DEFAULT_LANDING_ADDRESS_MAP_WIDTH,
  LANDING_ADDRESS_MAP_FIT_PADDING_PX,
  LANDING_ADDRESS_MAP_ZOOM,
} from "./landing-constant";
import type { IMarkerLocation } from "./landing-map.models";

interface IAddressBlockMapProps {
  locations?: IMarkerLocation[];
  width?: string;
  height?: string;
  readonly?: boolean;
  /** A click on the map, or a marker dropped somewhere new. */
  onLocationChange?: (coordinate: ICoordinate) => void;
}

/**
 * The landing pages' station chooser: the passenger's address and the stations they can pick from, each
 * naming itself in a popup when clicked.
 *
 * Markers carry their icon as a React element now. Google needed one as a URL, so the steps were
 * serialising an SVG component to a data URI purely to hand it over; MapLibre renders it directly. A
 * marker with no icon of its own gets the app's picked-address pin, which stands in for the default red
 * one Google used to draw.
 */
export const LandingAddressMap: FC<IAddressBlockMapProps> = ({
  locations = [],
  readonly,
  onLocationChange,
  width = DEFAULT_LANDING_ADDRESS_MAP_WIDTH,
  height = DEFAULT_LANDING_ADDRESS_MAP_HEIGHT,
}) => {
  const mapRef = useRef<MapRef>(null);
  const [selected, setSelected] = useState<ICoordinate | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const handleMapClick = useEvent((clicked: ICoordinate): void =>
    onLocationChange?.(clicked),
  );

  const handleDragEnd = useEvent(
    (event: { lngLat: { lat: number; lng: number } }): void =>
      onLocationChange?.({ lat: event.lngLat.lat, lng: event.lngLat.lng }),
  );

  const handlePinDragEnd = useEvent((coordinate: ICoordinate): void =>
    onLocationChange?.(coordinate),
  );

  const handleClosePopup = useEvent((): void => setSelected(null));

  /*
   * The click must not reach the map. MapLibre popups close themselves on a map click, so without this
   * the popup opens and is dismissed again by the very click that opened it.
   */
  const handleMarkerClick = useEvent(
    (event: { originalEvent: MouseEvent }, location: IMarkerLocation): void => {
      event.originalEvent.stopPropagation();
      setSelected(location.popupText ? location.coordinate : null);
    },
  );

  /*
   * Framed through the shared hook rather than a local `fitBounds`: it parks a fit that arrives before the
   * container has a size and completes it on the next resize, which a bare call silently drops. The landing
   * map is a fixed-height box that can lay out after its first render, so that case is reachable here.
   */
  const fitLocations = useMemo<ILocationInfo[]>(
    () =>
      locations.flatMap(({ coordinate }) =>
        coordinate
          ? [{ latitude: coordinate.lat, longitude: coordinate.lng }]
          : [],
      ),
    [locations],
  );

  useMapLibreLocationsAutoFit({
    mapRef,
    locations: fitLocations,
    isMapLoaded,
    fitPadding: LANDING_ADDRESS_MAP_FIT_PADDING_PX,
  });

  return (
    <LandingAddressMapStyled
      $width={width}
      $height={height}
      mapRef={mapRef}
      onLoadedChange={setIsMapLoaded}
      initialCenter={
        locations.find(l => l?.isCenter)?.coordinate ?? defaultMapCenter
      }
      initialZoom={LANDING_ADDRESS_MAP_ZOOM}
      onMapClick={readonly ? undefined : handleMapClick}
      // Google rendered its default UI here, fullscreen button included.
      isFullscreenEnabled
      // A passenger's landing page has no company session to look places up in, nor stations to add.
      isPlaceInfoHidden
    >
      {isMapLoaded &&
        locations.map((location, index) => {
          if (!location.coordinate) {
            return null;
          }

          /*
           * Google anchored a custom icon at the bottom centre of its image, so a caller-supplied one still
           * sits there. The app's own pin must not: it is placed through the shared marker, whose zero-size
           * root puts the pin tip on the coordinate. Anchoring the bare SVG at "bottom" instead lands the
           * SVG's bottom edge there, which is below the tip once the badge is accounted for.
           */
          return location.icon ? (
            <Marker
              key={`marker-${index}`}
              latitude={location.coordinate.lat}
              longitude={location.coordinate.lng}
              anchor="bottom"
              draggable={!readonly}
              onDragEnd={handleDragEnd}
              onClick={event => handleMarkerClick(event, location)}
            >
              {location.icon}
            </Marker>
          ) : (
            <MapLibreStationPinMarker
              key={`marker-${index}`}
              latitude={location.coordinate.lat}
              longitude={location.coordinate.lng}
              type={MarkerIconType.Point}
              color={MAPLIBRE_PICKED_ADDRESS_PIN_COLOR}
              label={MAPLIBRE_PICKED_ADDRESS_PIN_GLYPH}
              isDraggable={!readonly}
              onDragEnd={handlePinDragEnd}
              onClick={event => handleMarkerClick(event, location)}
            />
          );
        })}

      {isMapLoaded &&
        locations.map(({ coordinate, popupText }, index) =>
          popupText && coordinate && isSameCoordinate(selected, coordinate) ? (
            <LandingAddressMapPopup
              key={`popup-${index}`}
              coordinate={coordinate}
              text={popupText}
              onClose={handleClosePopup}
            />
          ) : null,
        )}
    </LandingAddressMapStyled>
  );
};
